import dayjs from 'dayjs';
import z from 'zod';

const get = {
  body: z.never(),
  response: z.object({
    id: z.number(),
    created_at: z.date(),
    updated_at: z.date(),
    qty_ordered: z.number(),
    unit_price: z.string().nullable(),
    needed_by: z.date().nullable(),
    promised_date: z.date().nullable().optional(),
    notes: z.string().nullable(),
    part_number_snapshot: z.string(),
    part_description_snapshot: z.string().nullable(),
    po: z.number(),
    part: z.number().nullable(),
  }),
  params: z.object({
    id: z.coerce.number(),
  }),
  query: z.never(),
};
const getSchema = z.object(get);
export type Get = z.infer<typeof getSchema>;

const put = {
  body: z.object({
    qty_ordered: z.number(),
    unit_price: z.string(),
    needed_by: z.string().refine((date) => dayjs(date).isValid(), {
      message: 'Select a valid date',
    }),
    notes: z.string().optional(),
    po: z.coerce.number(),
    part: z.coerce.number(),
  }),
  response: z.object({
    id: z.number(),
    created_at: z.date(),
    updated_at: z.date(),
    qty_ordered: z.number(),
    unit_price: z.string(),
    needed_by: z.date(),
    notes: z.string(),
    part_number_snapshot: z.string(),
    part_description_snapshot: z.string(),
    po: z.number(),
    part: z.number(),
  }),
  params: z.object({
    id: z.coerce.number(),
  }),
  query: z.never(),
};
const putSchema = z.object(put);
export type Put = z.infer<typeof putSchema>;

export const patch = {
  body: z
    .object({
      // HTML number inputs emit strings; coerce so Zod doesn't reject
      // "expected number, received string" when react-hook-form forwards the
      // raw value into validation.
      qty_ordered: z.coerce.number().int().positive({
        message: 'Quantity must be a positive whole number',
      }),
      // Decimal on the backend; accept either the raw string from <input type="number">
      // or a number, then re-stringify so DRF's DecimalField is happy.
      unit_price: z
        .union([z.string(), z.number()])
        .transform((v) => (typeof v === 'number' ? String(v) : v)),
      // needed_by is optional: an empty string means "no date" (persisted as
      // null). Only a non-empty value must be a valid date.
      needed_by: z
        .string()
        .refine((date) => date === '' || dayjs(date).isValid(), {
          message: 'Select a valid date',
        })
        .nullable(),
      notes: z.string().optional(),
      po: z.coerce.number(),
      // Nullable so manual lines (no system part) can clear the FK.
      part: z.coerce.number().nullable(),
      manual_part_number: z.string().trim().min(1),
      description: z.string().nullable(),
    })
    .partial(),
  response: z.object({
    id: z.number(),
    created_at: z.date(),
    updated_at: z.date(),
    qty_ordered: z.number(),
    unit_price: z.string().nullable(),
    needed_by: z.date().nullable(),
    promised_date: z.date().nullable().optional(),
    notes: z.string().nullable(),
    part_number_snapshot: z.string(),
    part_description_snapshot: z.string().nullable(),
    po: z.number(),
    part: z.number().nullable(),
  }),
  params: z.object({
    id: z.coerce.number(),
  }),
  query: z.never(),
};
const patchSchema = z.object(patch);
export type Patch = z.infer<typeof patchSchema>;

// POST /procurement/purchase-orders/{po_id}/lines/
// Add one or more new lines to an existing purchase order. Mirrors the draft
// order line create payload so the create-flow row UI can be reused with
// minimal transformation.
export const post = {
  body: z.array(
    z
      .object({
        // Either `part` (FK id for system parts) or `manual_part_number`
        // (free-text manual line). Validation that exactly one is provided
        // is enforced by the backend; we keep both optional here so the
        // create-flow row UI can drive either path.
        part: z.coerce.number().int().positive().nullable().optional(),
        manual_part_number: z.string().optional(),
        description: z.string().nullable().optional(),
        qty: z.coerce.number().int().positive({
          message: 'Quantity must be a positive whole number',
        }),
        unit_price: z
          .union([z.string(), z.number()])
          .transform((v) => (typeof v === 'number' ? String(v) : v))
          .optional(),
        needed_by: z
          .string()
          .refine((date) => dayjs(date).isValid(), {
            message: 'Select a valid date',
          })
          .nullable()
          .optional(),
        promised_date: z
          .string()
          .refine((date) => dayjs(date).isValid(), {
            message: 'Select a valid date',
          })
          .nullable()
          .optional(),
        notes: z.string().optional(),
        // Optional release id; backend appends it to PurchaseOrder.release_ids
        // (set-union) but does NOT persist it on the PO line itself (PO lines
        // have no release FK).
        release: z.coerce.number().int().positive().optional(),
      })
      .refine(
        (line) => {
          const hasPart = line.part != null && line.part > 0;
          const hasManual = Boolean(line.manual_part_number?.trim());
          return hasPart !== hasManual;
        },
        {
          message: 'Provide exactly one of part or manual_part_number',
          path: ['part'],
        },
      )
      .refine((line) => !line.part || line.description == null, {
        message: 'Description is only available for manual lines',
        path: ['description'],
      })
      .refine((line) => !line.release || Boolean(line.part), {
        message: 'Release is only available for linked lines',
        path: ['release'],
      }),
  ),
  response: z.object({
    detail: z.string(),
    errors: z
      .array(
        z.object({
          detail: z.string(),
        }),
      )
      .optional(),
  }),
  params: z.object({
    po_id: z.coerce.number(),
  }),
  query: z.never(),
};
const postSchema = z.object(post);
export type Post = z.infer<typeof postSchema>;

// DELETE /procurement/purchase-order-lines/{id}/
// Allowed only when:
//   - PO.status === 'DRAFT_PO'
//   - line.qty_received === 0
//   - no InventoryReceipt is linked to the line's InventoryExpectedReceipt(s)
// Backend enforces all three guards in a single transaction and also
// hard-deletes the paired InventoryExpectedReceipt rows.
//
// `delete` is a JS reserved word; we use `del` as the local identifier and
// re-export it under the `delete` key so the rest of the codebase can write
// `ProcurementType.PurchaseOrderLines.delete`.
const del = {
  body: z.never(),
  response: z.never(),
  params: z.object({
    id: z.coerce.number(),
  }),
  query: z.never(),
};
const delSchema = z.object(del);
export type Delete = z.infer<typeof delSchema>;
export { del as delete };
